import React, { Component } from "react";
import { connect } from "react-redux";
import { injectIntl, intlShape } from "@ai360/core/intl";
import { service, actions, model } from "./data";
import { withMasked, withApiResult, withCrud } from "~/hocs";
import { preventBubbleUp } from "~/admin/utils";
import { defaultRequestFilters, REQUEST_PAYLOAD_FILTER } from "./data/service";
import { messages } from "../i18n-messages";
import {
    getSurfaceDefaultsValidationConfirmRequired,
    getSurfaceDefaultsValidationSucceeded,
} from "./data/selectors";
import { getApiErrorCodeList } from "~/core/api/selectors";
import {
    setSurfaceDefaultsValidationConfirm,
    validateSurfaceDefaultsRequest,
} from "./data/actions";

// Components
import AddEditPanel from "./add-edit/add-edit-container";
import ImportExportHeader from "../../agBytes/components/import-export-header";
import { DataTable, DialogBox, Button, DialogBoxFooterType } from "~/core";
import SlidingPanel from "~/sliding-panel/sliding-panel";

import { SurfaceDefaultsLog } from "./surface-defaults-log";

interface ISurfaceDefaultsProps {
    actions: { fetch: (requestOptions: unknown) => unknown };
    addEditPanel?: { showAddEditPanel?: boolean; record?: Record<string, unknown> };
    apiErrors?: unknown[];
    closeSidePanel: () => void;
    deleteSelected: (options?: unknown) => void;
    fetchRecords?: () => void;
    hierarchy?: Record<string, unknown>;
    intl: intlShape;
    needs: (requests: unknown[]) => void;
    onFilterClear?: () => void;
    onSubmit: () => void;
    records?: unknown[];
    setSurfaceDefaultsValidationConfirm: (showConfirm: boolean) => void;
    showValidationConfirmDialog?: boolean;
    userRole: Record<string, unknown>;
    validateSurfaceDefaultsRequest: (payload: Record<string, unknown>) => void;
    validationSucceeded?: boolean;
}

interface ISurfaceDefaultsState {
    currentRecord: Record<string, unknown>;
    isModalOpen: boolean;
    showConfirmSaveDialog: boolean;
}

export class SurfaceDefaults_ extends Component<ISurfaceDefaultsProps, ISurfaceDefaultsState> {
    private tableFooterOptions: { label: string; action: () => void }[];

    constructor(props: ISurfaceDefaultsProps) {
        super(props);
        this.state = {
            currentRecord: {},
            isModalOpen: false,
            showConfirmSaveDialog: false,
        };
        this.tableFooterOptions = [
            {
                label: "Delete Selected",
                action: this.props.deleteSelected,
            },
        ];
    }

    onToggleModalClick = () => {
        this.setState({ isModalOpen: !this.state.isModalOpen });
    };

    onRecordChange = (record: Record<string, unknown> = {}) => {
        this.setState({ currentRecord: record || {} });
    };

    validateSurfaceDefaults = (recordOverride?: Record<string, unknown>) => {
        const { addEditPanel } = this.props;
        const requestModel =
            recordOverride ||
            this.state.currentRecord ||
            (addEditPanel && addEditPanel.record ? addEditPanel.record : {}) ||
            {};

        this.props.validateSurfaceDefaultsRequest(requestModel);
    };

    confirmSurfaceDefaultsSave = () => {
        this.setState({ showConfirmSaveDialog: false }, () => {
            this.props.onSubmit();
        });
    };

    onFilterChange = (data) => {
        const { needs, actions } = this.props;
        const requestOptions = {
            ...defaultRequestFilters,
            [REQUEST_PAYLOAD_FILTER]: {
                ...defaultRequestFilters[REQUEST_PAYLOAD_FILTER],
                [model.PROPS_ORG_LEVEL_GUID]: data ? data.orgLevelGuid : null,
            },
        };
        needs([actions.fetch(requestOptions)]);
    };

    deleteSelected = (options) => {
        let { selectedItems } = options;
        selectedItems = selectedItems[0];
        options = { ...options, selectedItems };
        this.props.deleteSelected(options);
    };

    componentDidUpdate(prevProps: ISurfaceDefaultsProps) {
        if (
            this.props.showValidationConfirmDialog &&
            !prevProps.showValidationConfirmDialog &&
            !this.state.showConfirmSaveDialog
        ) {
            this.setState({ showConfirmSaveDialog: true });
        }

        if (
            this.props.validationSucceeded !== prevProps.validationSucceeded &&
            this.props.validationSucceeded
        ) {
            this.props.onSubmit();
        }
    }

    render() {
        const addEditPanel = this.props.addEditPanel || {};
        const { showAddEditPanel = false } = addEditPanel;
        const { closeSidePanel, needs, userRole } = this.props;
        const { formatMessage } = this.props.intl;
        let slidingPanelProps = { ...this.props };
        if (showAddEditPanel) {
            slidingPanelProps = {
                ...slidingPanelProps,
                ...this.props.addEditPanel,
                onRecordChange: this.onRecordChange,
            };
        }
        return (
            <div className="content-table-container">
                <ImportExportHeader
                    {...this.props}
                    service={service}
                    hideActiveRecordLink
                    onToggleModalClick={this.onToggleModalClick}
                />

                <DataTable
                    {...this.props}
                    isEditable
                    isCheckbox={userRole[model.PROPS_PERSON_IMPORT_EXPORT]}
                    service={service}
                    messages={messages}
                    records={this.props.records}
                    footerOptions={this.tableFooterOptions}
                />
                {!showAddEditPanel ? null : (
                    <form onSubmit={(event) => preventBubbleUp(event)}>
                        <SlidingPanel
                            {...slidingPanelProps}
                            close={closeSidePanel}
                            component={AddEditPanel}
                            navigateTo={{
                                parentNameCode: "101",
                                childNameCode: "255",
                            }}
                        >
                            <Button
                                type={ButtonTypes.button}
                                value="save"
                                forceSubmit
                                onClick={() =>
                                    this.validateSurfaceDefaults(this.state.currentRecord)
                                }
                                testId="surface-defaults-save-button"
                            />
                            <Button
                                type={ButtonTypes.cancel}
                                onClick={() => this.props.closeSidePanel()}
                                testId="surface-defaults-cancel-button"
                            />
                        </SlidingPanel>
                    </form>
                )}
                <DialogBox
                    className="view-log-dialog-box"
                    isOpen={this.state.isModalOpen}
                    draggable
                    unrestricted
                    onClose={() => this.onToggleModalClick()}
                    title={formatMessage(messages.surfaceDefaultsLogHistory)}
                    testId="surface-defaults-log-history-modal"
                >
                    <SurfaceDefaultsLog needs={needs} />
                </DialogBox>
                <DialogBox
                    isOpen={this.state.showConfirmSaveDialog}
                    footerType={DialogBoxFooterType.YES_NO}
                    onAction={this.confirmSurfaceDefaultsSave}
                    onClose={() => {
                        this.setState({ showConfirmSaveDialog: false });
                        this.props.setSurfaceDefaultsValidationConfirm(false);
                    }}
                    title={formatMessage(messages.confirmationModalTitle)}
                >
                    <div>{formatMessage(messages.surfaceDefaultsDefinedLegendConfirmation)}</div>
                </DialogBox>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    apiErrors: getApiErrorCodeList(state),
    showValidationConfirmDialog: getSurfaceDefaultsValidationConfirmRequired(state),
    validationSucceeded: getSurfaceDefaultsValidationSucceeded(state),
});

const mapDispatchToProps = (dispatch) => ({
    setSurfaceDefaultsValidationConfirm: (showConfirm) =>
        dispatch(setSurfaceDefaultsValidationConfirm(showConfirm)),
    validateSurfaceDefaultsRequest: (payload) => dispatch(validateSurfaceDefaultsRequest(payload)),
});

export const SurfaceDefaults = injectIntl(
    withMasked(
        withApiResult(
            withCrud(
                connect(mapStateToProps, mapDispatchToProps)(SurfaceDefaults_),
                service,
                actions
            ),
            actions.importData
        )
    )
);
